import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Alert,
  Divider,
  List,
  ListItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ExpandMore,
  Save,
  Article,
  Quiz,
  Forum,
  Description,
} from '@mui/icons-material';
import { putItem } from '../../../support/queries';
import {
  SnackableCourseData,
  SnackableContentBlock,
  SnackablePage,
  SnackableSectionMeta,
} from '../../../types/moodleTypes';
import { useAuthenticateUser } from '../../../hooks/useAuthenticateUser';

interface Props {
  courseId: number;
  courseData: SnackableCourseData;
  originalCourseRecord?: any;
  onSave: () => void;
  onRevert?: () => Promise<void> | void;
  canRevert?: boolean;
}

export default function CourseContentEditor({
                                              courseId,
                                              courseData,
                                              originalCourseRecord,
                                              onSave,
                                              onRevert,
                                              canRevert = false,
                                            }: Props) {
  const [editedData, setEditedData] = useState<SnackableCourseData>(courseData);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [expandedSection, setExpandedSection] = useState<number | false>(0);
  const [expandedPage, setExpandedPage] = useState<string | false>(false);

  const [confirmOpen, setConfirmOpen] = useState(false);

  const { email } = useAuthenticateUser();

  useEffect(() => {
    setEditedData({
      ...courseData,
      fullname: courseData.fullname || '',
      shortname: courseData.shortname || '',
      summary: courseData.summary || '',
    });
    setHasChanges(false);
    setSaveSuccess(false);
    setError(null);
  }, [courseData]);

  const markDirty = () => {
    setHasChanges(true);
    setSaveSuccess(false);
  };

  const handleMetadataChange = (field: keyof SnackableCourseData, value: any) => {
    setEditedData({ ...editedData, [field]: value });
    markDirty();
  };

  const handleSectionChange = (sectionIndex: number, field: keyof SnackableSectionMeta, value: any) => {
    const newSections = [...editedData.sections];
    newSections[sectionIndex] = {
      ...newSections[sectionIndex],
      [field]: value,
    };
    setEditedData({ ...editedData, sections: newSections });
    markDirty();
  };

  const handlePageChange = (pageIndex: number, field: keyof SnackablePage, value: any) => {
    const newPages = [...(editedData.pages || [])];
    newPages[pageIndex] = {
      ...newPages[pageIndex],
      [field]: value,
    };
    setEditedData({ ...editedData, pages: newPages });
    markDirty();
  };

  const handleContentBlockChange = (
    pageIndex: number,
    blockIndex: number,
    field: keyof SnackableContentBlock,
    value: any
  ) => {
    const newPages = [...(editedData.pages || [])];
    const newContent = [...newPages[pageIndex].content];

    newContent[blockIndex] = {
      ...newContent[blockIndex],
      [field]: value,
    };

    newPages[pageIndex] = {
      ...newPages[pageIndex],
      content: newContent,
    };

    setEditedData({ ...editedData, pages: newPages });
    markDirty();
  };

  const handleListItemChange = (
    pageIndex: number,
    blockIndex: number,
    itemIndex: number,
    value: string
  ) => {
    const newPages = [...(editedData.pages || [])];
    const block = newPages[pageIndex].content[blockIndex];
    const newItems = [...(block.items || [])];
    newItems[itemIndex] = value;

    const newContent = [...newPages[pageIndex].content];
    newContent[blockIndex] = {
      ...newContent[blockIndex],
      items: newItems,
    };

    newPages[pageIndex] = {
      ...newPages[pageIndex],
      content: newContent,
    };

    setEditedData({ ...editedData, pages: newPages });
    markDirty();
  };

  const handleDiscardChanges = () => {
    setEditedData({
      ...courseData,
      fullname: courseData.fullname || '',
      shortname: courseData.shortname || '',
      summary: courseData.summary || '',
    });
    setHasChanges(false);
    setSaveSuccess(false);
    setError(null);
  };

  const handleRevert = async () => {
    if (!onRevert || !canRevert || reverting) return;

    try {
      setReverting(true);
      setError(null);
      await onRevert();
      setHasChanges(false);
      setSaveSuccess(false);
    } catch (err: any) {
      setError(err?.message || 'Failed to revert draft');
    } finally {
      setReverting(false);
    }
  };

  const handleSave = async () => {
    if (!hasChanges || saving) return;

    setSaving(true);
    setError(null);
    setSaveSuccess(false);

    try {
      await putItem('db/ai-snackable-course', {
        id: courseId,
        title: editedData.fullname || '',
        staged_course: editedData,
        updated_by: email
      });

      setHasChanges(false);
      setSaveSuccess(true);
      setConfirmOpen(false);

      onSave();
    } catch (err: any) {
      setError(err.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const renderContentBlockEditor = (
    block: SnackableContentBlock,
    pageIndex: number,
    blockIndex: number
  ) => {
    const blockContent = block.text || block.content || '';

    switch (block.type) {
      case 'heading':
        return (
          <TextField
            fullWidth
            label={`Heading (Level ${block.level || 2})`}
            value={blockContent}
            onChange={(e) =>
              handleContentBlockChange(pageIndex, blockIndex, block.text ? 'text' : 'content', e.target.value)
            }
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <Typography variant="subtitle2" sx={{ mr: 1, color: 'primary.main' }}>
                  H{block.level || 2}
                </Typography>
              ),
            }}
          />
        );

      case 'paragraph':
        return (
          <TextField
            fullWidth
            label="Paragraph"
            value={blockContent}
            onChange={(e) =>
              handleContentBlockChange(pageIndex, blockIndex, block.text ? 'text' : 'content', e.target.value)
            }
            variant="outlined"
            multiline
            rows={4}
            size="small"
            helperText="HTML tags like <b> and <i> are supported"
          />
        );

      case 'list':
        return (
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography variant="subtitle2" gutterBottom>
              {block.ordered ? 'Ordered List' : 'Unordered List'}
            </Typography>
            {block.items?.map((item, itemIndex) => (
              <TextField
                key={itemIndex}
                fullWidth
                size="small"
                value={item}
                onChange={(e) =>
                  handleListItemChange(pageIndex, blockIndex, itemIndex, e.target.value)
                }
                margin="dense"
                InputProps={{
                  startAdornment: (
                    <Typography sx={{ mr: 1 }}>
                      {block.ordered ? `${itemIndex + 1}.` : '•'}
                    </Typography>
                  ),
                }}
                helperText={itemIndex === 0 ? 'HTML tags like <b> are supported' : undefined}
              />
            ))}
          </Paper>
        );

      case 'callout':
      case 'note':
      case 'warning':
        return (
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              bgcolor:
                block.style === 'success'
                  ? 'success.light'
                  : block.style === 'warning'
                    ? 'warning.light'
                    : 'info.light',
            }}
          >
            <TextField
              fullWidth
              label="Title"
              value={block.title || ''}
              onChange={(e) =>
                handleContentBlockChange(pageIndex, blockIndex, 'title', e.target.value)
              }
              size="small"
              margin="dense"
            />
            <TextField
              fullWidth
              label="Content"
              value={blockContent}
              onChange={(e) =>
                handleContentBlockChange(pageIndex, blockIndex, block.text ? 'text' : 'content', e.target.value)
              }
              multiline
              rows={3}
              size="small"
              margin="dense"
            />
          </Paper>
        );

      case 'card':
        return (
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.100' }}>
            <TextField
              fullWidth
              label="Card Title"
              value={block.title || ''}
              onChange={(e) =>
                handleContentBlockChange(pageIndex, blockIndex, 'title', e.target.value)
              }
              size="small"
              margin="dense"
            />
            <TextField
              fullWidth
              label="Card Content (HTML allowed)"
              value={blockContent}
              onChange={(e) =>
                handleContentBlockChange(pageIndex, blockIndex, block.text ? 'text' : 'content', e.target.value)
              }
              multiline
              rows={4}
              size="small"
              margin="dense"
            />
          </Paper>
        );

      case 'code':
        return (
          <TextField
            fullWidth
            label="Code Block"
            value={blockContent}
            onChange={(e) =>
              handleContentBlockChange(pageIndex, blockIndex, block.text ? 'text' : 'content', e.target.value)
            }
            variant="outlined"
            multiline
            rows={6}
            size="small"
            sx={{ fontFamily: 'monospace', bgcolor: 'grey.100' }}
          />
        );

      case 'table':
        return (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Table (Advanced editing)
            </Typography>
            <Alert severity="info">
              Table editing coming soon. Current: {block.headers?.length || 0} columns, {block.rows?.length || 0} rows
            </Alert>
          </Paper>
        );

      default:
        return (
          <TextField
            fullWidth
            label={`${block.type} block`}
            value={blockContent}
            onChange={(e) =>
              handleContentBlockChange(pageIndex, blockIndex, block.text ? 'text' : 'content', e.target.value)
            }
            variant="outlined"
            multiline
            size="small"
          />
        );
    }
  };

  const getSectionContent = (sectionIndex: number) => {
    return {
      pages: editedData.pages?.filter((p) => p.section === sectionIndex) || [],
      quiz: editedData.quizzes?.find((q) => q.section === sectionIndex),
      forum: editedData.forums?.find((f) => f.section === sectionIndex),
      resources: editedData.resources?.filter((r) => r.section === sectionIndex) || [],
    };
  };

  return (
    <Box>
      <Paper
        elevation={2}
        sx={{
          mb: 2,
          position: 'sticky',
          top: 0,
          zIndex: 10,
          p: 1.5,
          borderLeft: '4px solid',
          borderColor: hasChanges ? 'warning.main' : 'divider',
          bgcolor: hasChanges ? 'warning.light' : 'background.paper',
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
          <Typography variant="body2" fontWeight={600}>
            {hasChanges ? 'You have unsaved changes' : 'No unsaved changes'}
          </Typography>

          <Box display="flex" gap={1}>
            {canRevert && (
              <Button
                variant="outlined"
                color="warning"
                onClick={handleRevert}
                disabled={saving || reverting}
              >
                {reverting ? 'Reverting...' : 'Revert Draft'}
              </Button>
            )}

            <Button variant="outlined" onClick={handleDiscardChanges} disabled={!hasChanges || saving || reverting}>
              Discard
            </Button>

            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={() => setConfirmOpen(true)}
              disabled={!hasChanges || saving || reverting}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </Box>
      </Paper>

      {saveSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaveSuccess(false)}>
          Draft changes saved successfully.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Course Information
        </Typography>
        <TextField
          fullWidth
          label="Course Title"
          value={editedData.fullname}
          onChange={(e) => handleMetadataChange('fullname', e.target.value)}
          margin="normal"
        />
        <TextField
          fullWidth
          label="Short Name"
          value={editedData.shortname}
          onChange={(e) => handleMetadataChange('shortname', e.target.value)}
          margin="normal"
          helperText="Used in breadcrumbs and navigation"
        />
        <TextField
          fullWidth
          label="Course Summary"
          value={editedData.summary}
          onChange={(e) => handleMetadataChange('summary', e.target.value)}
          margin="normal"
          multiline
          rows={3}
          helperText="Displayed on course listing pages"
        />
      </Paper>

      <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
        Course Sections ({editedData.sections.length})
      </Typography>

      {editedData.sections.map((section, sectionIndex) => {
        const sectionContent = getSectionContent(sectionIndex);

        return (
          <Accordion
            key={sectionIndex}
            expanded={expandedSection === sectionIndex}
            onChange={() =>
              setExpandedSection(expandedSection === sectionIndex ? false : sectionIndex)
            }
            sx={{ mb: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box display="flex" alignItems="center" gap={2} width="100%">
                <Typography variant="subtitle1" fontWeight={600}>
                  Section {sectionIndex}: {section.name}
                </Typography>
                <Box display="flex" gap={1}>
                  {sectionContent.pages.length > 0 && (
                    <Chip size="small" icon={<Article />} label={`${sectionContent.pages.length} pages`} />
                  )}
                  {sectionContent.quiz && (
                    <Chip size="small" icon={<Quiz />} label="Quiz" color="primary" />
                  )}
                  {sectionContent.forum && (
                    <Chip size="small" icon={<Forum />} label="Forum" color="secondary" />
                  )}
                </Box>
              </Box>
            </AccordionSummary>

            <AccordionDetails>
              <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                <TextField
                  fullWidth
                  label="Section Name"
                  value={section.name}
                  onChange={(e) => handleSectionChange(sectionIndex, 'name', e.target.value)}
                  margin="normal"
                  size="small"
                />
                <TextField
                  fullWidth
                  label="Section Summary"
                  value={section.summary || ''}
                  onChange={(e) => handleSectionChange(sectionIndex, 'summary', e.target.value)}
                  margin="normal"
                  multiline
                  rows={2}
                  size="small"
                />
              </Paper>

              {sectionContent.pages.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom color="primary">
                    Pages ({sectionContent.pages.length})
                  </Typography>

                  {sectionContent.pages.map((page, localPageIndex) => {
                    const globalPageIndex =
                      editedData.pages?.findIndex(
                        (p) => p.name === page.name && p.section === sectionIndex
                      ) ?? -1;

                    if (globalPageIndex === -1) return null;

                    const pageKey = `page-${sectionIndex}-${localPageIndex}`;

                    return (
                      <Accordion
                        key={pageKey}
                        expanded={expandedPage === pageKey}
                        onChange={() =>
                          setExpandedPage(expandedPage === pageKey ? false : pageKey)
                        }
                        sx={{ mb: 1, bgcolor: 'background.paper' }}
                      >
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Article fontSize="small" color="action" />
                            <Typography variant="body2" fontWeight={500}>
                              {page.name}
                            </Typography>
                            <Chip size="small" label={`${page.content.length} blocks`} variant="outlined" />
                          </Box>
                        </AccordionSummary>

                        <AccordionDetails>
                          <Paper variant="outlined" sx={{ p: 2 }}>
                            <TextField
                              fullWidth
                              label="Page Title"
                              value={page.name}
                              onChange={(e) => handlePageChange(globalPageIndex, 'name', e.target.value)}
                              margin="normal"
                              size="small"
                              sx={{ mb: 2 }}
                            />

                            <Divider sx={{ my: 2 }} />

                            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                              Content Blocks ({page.content.length})
                            </Typography>

                            {page.content.map((block, blockIndex) => (
                              <Paper key={blockIndex} variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                  <Chip label={block.type} size="small" variant="outlined" color="primary" />
                                  <Typography variant="caption" color="text.secondary">
                                    Block {blockIndex + 1} of {page.content.length}
                                  </Typography>
                                </Box>
                                {renderContentBlockEditor(block, globalPageIndex, blockIndex)}
                              </Paper>
                            ))}
                          </Paper>
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </Box>
              )}

              {sectionContent.quiz && (
                <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: 'primary.light' }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Quiz color="primary" />
                    <Typography variant="subtitle2">{sectionContent.quiz.name}</Typography>
                    <Chip size="small" label={`${sectionContent.quiz.questions.length} questions`} />
                  </Box>
                  <Alert severity="info">
                    Quiz editing coming soon. Questions will be deployed as-is.
                  </Alert>
                </Paper>
              )}

              {sectionContent.forum && (
                <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: 'secondary.light' }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Forum color="secondary" />
                    <Typography variant="subtitle2">{sectionContent.forum.name}</Typography>
                  </Box>
                  <Alert severity="info">
                    Forum configuration cannot be edited here.
                  </Alert>
                </Paper>
              )}

              {sectionContent.resources.length > 0 && (
                <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Description />
                    <Typography variant="subtitle2">
                      Resources ({sectionContent.resources.length})
                    </Typography>
                  </Box>
                  <List dense>
                    {sectionContent.resources.map((resource, idx) => (
                      <ListItem key={idx}>
                        <Typography variant="body2">
                          <strong>{resource.name}:</strong> {resource.reference}
                        </Typography>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}

      <Dialog open={confirmOpen} onClose={() => !saving && setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Save</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Apply your draft changes now? This will update <strong>staged_course</strong> for this course record.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !hasChanges}>
            {saving ? 'Saving...' : 'Confirm Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}